<template>
  <div class="expansion-panel-list">
    <section
      v-for="(item, index) in normalizedItems"
      :key="item.id ?? index"
      class="expansion-panel"
      :class="{
        'expansion-panel--expanded': isExpanded(index),
        'expansion-panel--disabled': item.disabled
      }"
    >
      <button
        type="button"
        class="expansion-panel__header"
        :disabled="item.disabled"
        :aria-expanded="isExpanded(index) ? 'true' : 'false'"
        :aria-controls="getContentId(index)"
        @click="togglePanel(index)"
      >
        <span class="expansion-panel__header-text">
          <slot
            name="title"
            :item="item"
            :index="index"
            :expanded="isExpanded(index)"
          >
            <span class="expansion-panel__title">{{ item.title }}</span>
            <span
              v-if="item.subtitle"
              class="expansion-panel__subtitle"
            >
              {{ item.subtitle }}
            </span>
          </slot>
        </span>
        <NavigationExpandMore class="expansion-panel__icon" />
      </button>

      <div
        :id="getContentId(index)"
        class="expansion-panel__content"
        :class="{ 'expansion-panel__content--expanded': isExpanded(index) }"
      >
        <div class="expansion-panel__content-inner">
          <slot
            :item="item"
            :index="index"
            :expanded="isExpanded(index)"
          >
            <div
              v-if="item.data"
              class="expansion-panel__content-text"
              v-html="item.data"
            />
          </slot>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import NavigationExpandMore from '~/assets/icons/navigation_expand_more.svg?component'

type ExpansionPanelItem = {
    id?: string | number
    title: string
    subtitle?: string
    data?: string
    disabled?: boolean
}

const props = withDefaults(defineProps<{
    items: ExpansionPanelItem[]
    modelValue?: number | null
    defaultOpenIndex?: number
}>(), {
  modelValue: undefined,
  defaultOpenIndex: 0
})

const emit = defineEmits<{
    'update:modelValue': [value: number | null]
    change: [payload: { index: number | null, item: ExpansionPanelItem | null }]
}>()

const normalizedItems = computed(() => props.items ?? [])
const isControlled = computed(() => props.modelValue !== undefined)

const getInitialOpenIndex = () => {
  if (!normalizedItems.value.length) {
    return null
  }

  const requestedIndex = props.defaultOpenIndex
  return requestedIndex >= 0 && requestedIndex < normalizedItems.value.length
    ? requestedIndex
    : 0
}

const internalOpenIndex = ref<number | null>(getInitialOpenIndex())

const openIndex = computed(() => {
  return isControlled.value ? props.modelValue ?? null : internalOpenIndex.value
})

const isExpanded = (index: number) => openIndex.value === index

const updateOpenIndex = (index: number | null) => {
  if (!isControlled.value) {
    internalOpenIndex.value = index
  }

  emit('update:modelValue', index)
  emit('change', {
    index,
    item: index === null ? null : normalizedItems.value[index] ?? null
  })
}

const togglePanel = (index: number) => {
  if (normalizedItems.value[index]?.disabled) {
    return
  }

  updateOpenIndex(openIndex.value === index ? null : index)
}

const getContentId = (index: number) => `expansion-panel-content-${index}`

watch(
  normalizedItems,
  (items) => {
    if (!items.length) {
      updateOpenIndex(null)
      return
    }

    const currentIndex = openIndex.value
    if (currentIndex === null || currentIndex >= items.length) {
      updateOpenIndex(getInitialOpenIndex())
    }
  },
  {immediate: true}
)
</script>

<style lang="scss" scoped>
.expansion-panel-list {
  display: flex;
  flex-direction: column;
}

.expansion-panel {
  border: 0.0625rem solid $color--input__border;
  background-color: $color__white;
  overflow: hidden;

  &:not(:last-child) {
    border-bottom: none;
  }

  &--disabled {
    opacity: .6;
  }

  &__header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: .75rem $default-margin-padding-width;
    border: 0;
    background-color: $color__content--background__primary;
    color: $color__primary;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
      outline: 0.125rem solid $color__primary;
      outline-offset: -0.125rem;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__header-text {
    display: flex;
    flex-direction: column;
    gap: .1875rem;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.875rem;
    line-height: 1.1375rem;
    font-weight: $base-text-font-weight-bold;
  }

  &__subtitle {
    font-size: 0.875rem;
    line-height: 1.35;
    font-weight: $base-text-font-weight-light;
    color: $color__fst_gray__100;
  }

  &__icon {
    flex-shrink: 0;
    font-size: 1.5rem;
    transition: transform .25s ease;
  }

  &__content {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows .25s ease, opacity .2s ease;
    opacity: 0;
  }

  &__content--expanded {
    grid-template-rows: 1fr;
    opacity: 1;
  }

  &__content-inner {
    min-height: 0;
    overflow: hidden;
    padding: 0;
  }

  &__content-text {
    font-weight: $base-text-font-weight-light;
    line-height: 1.138rem;

    :deep(ul) {
      margin-block: 0;
      padding-inline: $default-margin-padding-width * 1.25;
    }

    :deep(li::marker) {
      font-size: .5rem; /* Indirectly increases width by making the marker larger */
    }
  }

  &:has(+ .expansion-panel--expanded) {
    border-bottom: 0.0625rem solid $color--input__border;
  }

  &--expanded {
    margin-block: .5rem;

    &:not(:last-child) {
      border-bottom: 0.0625rem solid $color--input__border;
    }

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }

    .expansion-panel__icon {
      transform: rotate(180deg);
    }

    .expansion-panel__content-inner {
      padding: $default-margin-padding-width;
    }
  }
}
</style>
